import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  ArrowCounterClockwise,
  ArrowUp,
  Camera,
  ChatCircleDots,
  Headset,
  Microphone,
  Plus,
  Sparkle,
  Stop,
  Trash,
} from "@phosphor-icons/react";
import { AppHeader } from "@/components/AppHeader";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Bubble, ProposalCard, useAssistant } from "@/lib/assistantShared";

const IDEAS = [
  "Quote: John Doe, 208 sqft, 5 slabs Taj Mahal at $4980 each, mitered edge, no add-ons",
  "What quartzite do we have over 40 sq ft?",
  "Schedule an install for the Robinson job next Tuesday",
  "Add a reminder task to call Maria about her quote tomorrow",
  "Move JOB-1002's install to next Friday",
];

export default function ShopAssistant() {
  const [cfg, setCfg] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [actions, setActions] = useState([]);
  const [draft, setDraft] = useState("");
  const endRef = useRef(null);

  const loadSessions = useCallback(() => {
    api
      .get("/ai/chats")
      .then(({ data }) => setSessions(data.sessions || []))
      .catch(() => setSessions([]));
  }, []);

  const loadActions = useCallback(() => {
    api
      .get("/ai/actions")
      .then(({ data }) => setActions(data.actions || []))
      .catch(() => setActions([]));
  }, []);

  const {
    messages,
    setMessages,
    busy,
    sessionId,
    setSessionId,
    send,
    sendSketch,
    confirmProp,
    cancelProp,
    reset,
    uid,
  } = useAssistant({ onActed: loadActions });
  const sketchRef = useRef(null);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const recRef = useRef(null);
  const [handsFree, setHandsFree] = useState(
    () => localStorage.getItem("sw_ai_handsfree") === "1",
  );
  const toggleHandsFree = () => {
    const next = !handsFree;
    setHandsFree(next);
    localStorage.setItem("sw_ai_handsfree", next ? "1" : "0");
    if (!next) window.speechSynthesis?.cancel();
    toast.success(
      next
        ? "Hands-free on: voice notes send themselves and replies are read aloud"
        : "Hands-free off",
    );
  };
  const speak = (text) => {
    if (!handsFree || !window.speechSynthesis || !text) return;
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(
      text.replace(/[•*#_]/g, "").slice(0, 600),
    );
    u.rate = 1.05;
    window.speechSynthesis.speak(u);
  };
  const lastSpoken = useRef(null);
  useEffect(() => {
    const last = messages[messages.length - 1];
    if (!last || last === lastSpoken.current || last.role === "user") return;
    lastSpoken.current = last;
    if (last.kind === "proposal")
      speak(`${last.summary}. Tap confirm to go ahead.`);
    else speak(last.text);
  }, [messages]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleVoice = async () => {
    if (recording) {
      recRef.current?.stop();
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      const chunks = [];
      rec.ondataavailable = (e) => e.data.size && chunks.push(e.data);
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setRecording(false);
        setTranscribing(true);
        try {
          const fd = new FormData();
          fd.append(
            "file",
            new Blob(chunks, { type: rec.mimeType || "audio/webm" }),
            "voice.webm",
          );
          const { data } = await api.post("/ai/transcribe", fd);
          if (data.text) {
            if (handsFree) {
              setDraft("");
              await send(data.text);
              loadSessions();
            } else {
              setDraft((d) => (d ? `${d} ${data.text}` : data.text));
              toast.success("Got it — check the text, then send");
            }
          } else toast.error("Nothing heard — try again closer to the mic");
        } catch (err) {
          toast.error(
            formatApiErrorDetail(err.response?.data?.detail) ||
              "Could not transcribe",
          );
        }
        setTranscribing(false);
      };
      recRef.current = rec;
      rec.start();
      setRecording(true);
    } catch {
      toast.error(
        "Microphone not available — allow mic access in your browser",
      );
    }
  };

  const onSketch = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const note = draft.trim();
    setDraft("");
    await sendSketch(file, note);
    loadSessions();
  };

  useEffect(() => {
    api
      .get("/settings/ai-text")
      .then(({ data }) => setCfg(data.config))
      .catch(() => setCfg({ enabled: false }));
    loadSessions();
    loadActions();
  }, [loadSessions, loadActions]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const openSession = async (id) => {
    setSessionId(id);
    const { data } = await api.get(`/ai/chat/${id}`);
    setMessages(
      (data.messages || []).map((m) => ({ ...m, key: `h${uid.current++}` })),
    );
  };

  const submit = async (text) => {
    const q = (text ?? draft).trim();
    if (!q || busy) return;
    setDraft("");
    await send(q);
    loadSessions();
  };

  const wipe = async (id) => {
    await api.delete(`/ai/chat/${id}`);
    if (id === sessionId) reset();
    loadSessions();
  };

  const undoAction = async (a) => {
    try {
      const { data } = await api.post(`/ai/actions/${a.id}/undo`);
      toast.success(data.message || "Undone");
      loadActions();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || "Could not undo",
      );
    }
  };

  const fmtTime = (iso) => {
    try {
      return new Date(iso).toLocaleString([], {
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return "";
    }
  };

  return (
    <div className="grain min-h-screen">
      <AppHeader title="Assistant" />
      <main className="relative z-10 pb-10 pt-6" data-testid="shop-assistant">
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-[16rem_1fr] lg:items-start">
          <aside className="min-w-0 rounded-lg border border-zinc-800 bg-zinc-900 p-3">
            <button
              type="button"
              data-testid="ai-new-chat"
              onClick={reset}
              className="flex w-full items-center gap-2 rounded-lg bg-indigo-500 px-3 py-2.5 text-[11px] font-bold uppercase tracking-widest text-white transition-colors duration-150 hover:bg-indigo-400"
            >
              <Plus size={13} weight="bold" /> New chat
            </button>
            <div className="mt-3 space-y-1" data-testid="ai-sessions">
              {sessions.map((s) => (
                <div
                  key={s.session_id}
                  className="group flex items-center gap-1"
                >
                  <button
                    type="button"
                    data-testid={`ai-session-${s.session_id}`}
                    onClick={() => openSession(s.session_id)}
                    className={`min-w-0 flex-1 truncate rounded-lg px-2.5 py-2 text-left text-xs transition-colors duration-150 ${
                      s.session_id === sessionId
                        ? "bg-zinc-800 text-zinc-100"
                        : "text-zinc-500 hover:bg-zinc-800/60 hover:text-zinc-200"
                    }`}
                  >
                    {s.title || "Chat"}
                  </button>
                  <button
                    type="button"
                    aria-label="Delete chat"
                    data-testid={`ai-session-delete-${s.session_id}`}
                    onClick={() => wipe(s.session_id)}
                    className="shrink-0 p-1.5 text-zinc-700 opacity-0 transition-opacity duration-150 hover:text-red-400 group-hover:opacity-100"
                  >
                    <Trash size={13} weight="bold" />
                  </button>
                </div>
              ))}
              {sessions.length === 0 && (
                <p className="px-2 py-3 text-xs text-zinc-600">No chats yet.</p>
              )}
            </div>

            {actions.length > 0 && (
              <div
                className="mt-4 border-t border-zinc-800 pt-3"
                data-testid="ai-actions-panel"
              >
                <div className="px-1 text-[10px] font-bold uppercase tracking-widest text-zinc-600">
                  Recent actions
                </div>
                <div className="mt-2 space-y-1.5">
                  {actions.slice(0, 8).map((a) => (
                    <div
                      key={a.id}
                      data-testid={`ai-action-${a.id}`}
                      className="rounded-lg border border-zinc-800/70 bg-zinc-950/50 p-2"
                    >
                      <p
                        className={`text-[11px] leading-snug ${
                          a.undone
                            ? "text-zinc-600 line-through"
                            : "text-zinc-300"
                        }`}
                      >
                        {a.message}
                      </p>
                      <div className="mt-1 flex items-center justify-between gap-2">
                        <span className="truncate text-[10px] text-zinc-600">
                          {fmtTime(a.at)}
                        </span>
                        {a.undoable && (
                          <button
                            type="button"
                            data-testid={`ai-undo-${a.id}`}
                            onClick={() => undoAction(a)}
                            className="flex shrink-0 items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-indigo-400 transition-colors duration-150 hover:text-indigo-300"
                          >
                            <ArrowCounterClockwise size={11} weight="bold" />{" "}
                            Undo
                          </button>
                        )}
                        {a.undone && (
                          <span className="shrink-0 text-[10px] text-zinc-600">
                            Undone
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </aside>

          <section className="flex min-h-[70vh] min-w-0 flex-col rounded-lg border border-zinc-800 bg-zinc-950">
            {cfg && !cfg.enabled && (
              <div
                className="m-4 rounded-lg border border-amber-500/40 bg-amber-500/10 p-4 text-sm text-amber-200"
                data-testid="ai-off-warning"
              >
                The assistant is switched off. Turn it on in Settings → AI
                Assistant.
              </div>
            )}

            <div className="flex flex-1 flex-col gap-3 overflow-y-auto p-5">
              {messages.length === 0 && (
                <div className="m-auto max-w-lg text-center">
                  <ChatCircleDots
                    size={26}
                    weight="bold"
                    className="mx-auto text-indigo-400"
                  />
                  <h2 className="mt-3 text-lg font-bold text-zinc-200">
                    Ask, or ask me to do it
                  </h2>
                  <p className="mt-1 text-sm text-zinc-500">
                    Give me a job — client, sqft, slab count and cost, material,
                    edge — and I'll run it through your pricing rules and save a
                    draft quote. I also read live stock, schedule jobs, move
                    dates, add tasks, fixtures, notes and reminders — always
                    showing the plan first and waiting for your OK.
                  </p>
                  <div className="mt-5 flex flex-wrap justify-center gap-2">
                    {IDEAS.map((idea) => (
                      <button
                        key={idea}
                        type="button"
                        data-testid={`ai-idea-${idea.slice(0, 12)}`}
                        onClick={() => submit(idea)}
                        className="rounded-full border border-zinc-800 bg-zinc-900 px-4 py-2 text-xs text-zinc-400 transition-colors duration-150 hover:border-indigo-400/50 hover:text-zinc-100"
                      >
                        {idea}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {messages.map((m) =>
                m.kind === "proposal" ? (
                  <ProposalCard
                    key={m.key}
                    item={m}
                    onConfirm={confirmProp}
                    onCancel={cancelProp}
                  />
                ) : (
                  <Bubble key={m.key} role={m.role} text={m.text} />
                ),
              )}
              {busy && (
                <div
                  className="self-start rounded-2xl border border-zinc-800 bg-zinc-900 px-4 py-3 text-sm text-zinc-500"
                  data-testid="ai-thinking"
                >
                  Thinking…
                </div>
              )}
              <div ref={endRef} />
            </div>

            <div className="flex flex-wrap items-end gap-2 border-t border-zinc-800 p-3 sm:flex-nowrap sm:p-4">
              <input
                ref={sketchRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                data-testid="ai-sketch-input"
                onChange={onSketch}
              />
              <div className="order-2 flex w-full shrink-0 items-center gap-2 sm:order-1 sm:w-auto">
                <button
                  type="button"
                  data-testid="ai-sketch-button"
                  title="Quote from a sketch photo — type the client / material / slab cost first, then attach"
                  disabled={busy}
                  onClick={() => sketchRef.current?.click()}
                  className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-zinc-800 bg-zinc-900 text-zinc-300 transition-colors duration-150 hover:border-indigo-400/60 hover:text-white disabled:opacity-40 sm:h-[52px] sm:w-[52px]"
                >
                  <Camera size={18} weight="bold" />
                </button>
                <button
                  type="button"
                  data-testid="ai-voice-button"
                  title={
                    recording
                      ? "Tap to stop and transcribe"
                      : "Dictate the job details"
                  }
                  disabled={busy || transcribing}
                  onClick={toggleVoice}
                  className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border transition-colors duration-150 disabled:opacity-40 sm:h-[52px] sm:w-[52px] ${
                    recording
                      ? "border-red-500 bg-red-500/20 text-red-200 animate-pulse"
                      : "border-zinc-800 bg-zinc-900 text-zinc-300 hover:border-indigo-400/60 hover:text-white"
                  }`}
                >
                  {recording ? (
                    <Stop size={18} weight="fill" />
                  ) : (
                    <Microphone size={18} weight="bold" />
                  )}
                </button>
                <button
                  type="button"
                  data-testid="ai-handsfree-toggle"
                  title="Hands-free: voice notes send without tapping, replies are read aloud"
                  onClick={toggleHandsFree}
                  className={`flex h-11 shrink-0 items-center justify-center gap-1.5 rounded-lg border px-3 text-[10px] font-bold uppercase tracking-widest transition-colors duration-150 sm:h-[52px] sm:flex-col sm:gap-0 sm:px-2 sm:text-[9px] ${
                    handsFree
                      ? "border-emerald-500/60 bg-emerald-500/15 text-emerald-200"
                      : "border-zinc-800 bg-zinc-900 text-zinc-500 hover:text-zinc-200"
                  }`}
                >
                  <Headset size={16} weight="bold" />
                  <span>{handsFree ? "Hands-free" : "Tap send"}</span>
                </button>
              </div>
              <textarea
                data-testid="ai-input"
                rows={2}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    submit();
                  }
                }}
                placeholder={
                  transcribing
                    ? "Transcribing your voice note…"
                    : "Ask about stock, quote a job, or dictate…"
                }
                className="order-1 min-h-[52px] min-w-0 flex-1 resize-none rounded-lg border border-zinc-800 bg-zinc-900 p-3 text-base text-zinc-200 outline-none focus:border-indigo-500 sm:order-2 sm:text-sm"
              />
              <button
                type="button"
                data-testid="ai-send"
                disabled={busy || !draft.trim()}
                onClick={() => submit()}
                className="order-1 flex h-[52px] w-[52px] shrink-0 items-center justify-center rounded-lg bg-indigo-500 text-white transition-colors duration-150 hover:bg-indigo-400 disabled:opacity-40 sm:order-3"
              >
                {busy ? (
                  <Sparkle size={16} weight="bold" className="animate-pulse" />
                ) : (
                  <ArrowUp size={17} weight="bold" />
                )}
              </button>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
}
